/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Airport } from './../../../models/airport';
import { AirportListService } from './../../../services/airport/airport-list.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { InfiniteScrollCustomEvent, ModalController, IonSearchbar } from '@ionic/angular';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { localStorageKeys } from 'src/app/services/localStorage/localStorageVariables';
import { ApiErrorService } from 'src/app/services/api-error.service';
@Component({
  selector: 'app-airport-list',
  templateUrl: './airport-list.component.html',
  styleUrls: ['./airport-list.component.scss'],
})
export class AirportListComponent implements OnInit, OnDestroy {
  @Input() type: string;
  @Input() closeFlag: boolean;
  @Input() fareType: any;
  @Input() showLocalOfferTag?: boolean = true;
  airportList: Airport[];
  public noData: number;
  public page: number;
  public count: number;
  public currentCount: number;
  public searchTerm: string;
  public currentHeaderData: any;
  private airportSubscription: Subscription;
  private searchSubscription: Subscription;
  private searchSubject = new Subject<string>();
  private homeAirportExist: boolean;
  public hasError: boolean = false;
  private errorSubscription: Subscription;
  @ViewChild('airportSearch', { static: false }) searchbar: IonSearchbar;

  getPlaceholderText(): string {
    if (this.fareType === 'flexi_cancel') {
      return 'airport_list.search_by_airport__code_or_city_domestic_only';
    }
    return 'airport_list.search_by_airport__code_or_city';
  }

  // tslint:disable-next-line: max-line-length
  constructor(
    private airportListService: AirportListService,
    private modalController: ModalController,
    private analyticsService: AnalyticsService,
    private localStorageService: LocalStorageService,
    private apiErrorService: ApiErrorService
  ) {
    this.noData = 0;
    this.airportList = [];
    this.homeAirportExist = this.airportListService.getSelectedAirport()
      ? true
      : false;
  }

  ngOnInit() {
    this.page = 1;
    this.count = 20;
    this.currentCount = 20;
    this.searchTerm = '';
    this.currentHeaderData = {
      modalSlotEnd: this.closeFlag ? false : true,
      marl20: true,
      title: 'airport_list.header',
    };
    this.setupDebouncedSearch();
    this.getAirports();

    this.errorSubscription = this.apiErrorService.value.subscribe((val) => {
      this.hasError = val;
    });
  }

  ionViewDidEnter(): void {
    try {
      this.searchbar?.setFocus();
    } catch (e) {
    }
  }

  setupDebouncedSearch() {
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map((searchTerm: string) => searchTerm.trim()),
        switchMap((searchTerm: string) => {
          this.searchTerm = searchTerm;
          this.airportList = [];
          this.noData = 0;
          this.page = 1;
          this.count = 20;

          return this.airportListService.getAllAirports(
            this.searchTerm,
            this.type?.toLowerCase(),
            this.page,
            this.count,
            this.fareType
          );
        })
      )
      .subscribe((resData: any) => {
        const receivedAirportList = resData?.result || [];
        if (receivedAirportList && receivedAirportList.length > 0) {
          this.currentCount = receivedAirportList.length;
          this.airportList = receivedAirportList;
          this.hasError = false;
        } else {
          this.noData = 1;
          this.analyticsService.trackResponseError(
            {
              apiName: 'getAllAirports',
              message: 'API responded with empty payload',
              statusCode: 200,  
              servicetype: this.type?.toLowerCase(),
            },
            'Airport List - Search No Data'
          );
        }
      });
  }

  getAirports() {
    this.airportListService
      .getAllAirports(
        this.searchTerm,
        this.type?.toLowerCase(),
        this.page,
        this.count,
        this.fareType
      )
      .subscribe({
        next: (resData: any) => {
          const receivedAirportList = resData?.result || [];
          if (receivedAirportList && receivedAirportList.length > 0) {
            this.currentCount = receivedAirportList.length;
            this.airportList = receivedAirportList;
            this.hasError = false;
          } else {
            this.noData = 1;
          }
        },
        error: (err) => {
          this.airportList = [];
          this.noData = 1;
          this.analyticsService.trackResponseError(
            {
              message: err.message,
              statusCode: err.status,
              apiName: 'getAllAirports'
            },
            {
              search_term: this.searchTerm,
              type: this.type?.toLowerCase(),
              page: this.page,
              count: this.count,
            }
          );
        },
      });
  }

  closeModal() {
    this.modalController.dismiss();
    this.analyticsService.trackUnified('airport_list_closed', {
      flow: 'airport',
      outcome: 'success',
      legacy_event_name: 'button_clicked',
      component: 'airport-list-component',
      button: 'close-modal-button',
    });
  }
  async selectAiport(airport) {
    this.airportListService.setSelectedAirport(airport.airportCode);
    await this.updateLocationData(airport);
    this.modalController.dismiss({
      airport,
    });
  }

  filterAirportLIst(event) {
    const searchTerm = event.detail.value;
    this.searchSubject.next(searchTerm);
  }

  ionViewDidLeave() {
    if (this.airportSubscription) {
      this.airportSubscription.unsubscribe();
    }

    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.airportSubscription) {
      this.airportSubscription.unsubscribe();
    }
  }

  filterAirportLIstOnEnter(event) {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.searchSubject.next(searchTerm);
  }

  getNextPage(event) {
    if (this.currentCount < this.count) {
      (event as InfiniteScrollCustomEvent).target.complete();
      return;
    }
    this.page++;
    this.airportListService
      .getAllAirports(
        this.searchTerm,
        this.type?.toLowerCase(),
        this.page,
        this.count,
        this.fareType
      )
      .subscribe((resData: any) => {
        const receivedAirportList = resData?.result || [];
        if (receivedAirportList && receivedAirportList.length > 0) {
          this.currentCount = receivedAirportList.length;
          this.airportList = [...this.airportList, ...receivedAirportList];
        } else {
          this.noData = 1;
        }
        (event as InfiniteScrollCustomEvent).target.complete();
        if (this.currentCount < this.count) {
          event.target.disabled = true;
        } else {
          event.target.disabled = false;
        }
      });
  }
  updateLocationData(airport) {
    const address = {
      street: '',
      line2: '',
      landmark: '',
      city_name: airport?.city,
      postal_code: '',
      formatted_address: airport?.address,
    };

    this.localStorageService.setItem(
      localStorageKeys.ADDRESS,
      JSON.stringify(address)
    );

    const data = {
      latitude: airport.latitude,
      longitude: airport.longitude,
    };
    this.localStorageService.setItem(
      localStorageKeys.LAT_LONG_DATA,
      JSON.stringify(data)
    );
  }
}
